import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Checkbox from '@adsk/bim360-matrix-react-checkbox';
import Label from '@adsk/bim360-matrix-react-label';
import OverflowTooltip from '@adsk/bim360-matrix-react-overflow-tooltip';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import StatusColorIndicator from '@adsk/bim360-matrix-react-status-color-indicator/es/StatusColorIndicator';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { Colors } from '@adsk/bim360-matrix-react-helpers/es/consts';
import { ENTITY_TYPES } from '@adsk/bim360-matrix-react-entity-linker';

import './_EntityPickerItem.scss';

const EntityPickerItem = ({
  className,
  entityType,
  id,
  statusText,
  statusColor,
  userFriendlyId,
  title,
  isSelected,
  disabled,
  onChange
}) => {
  const inputId = `EntityPickerItem__type-${entityType}__id-${id}`;
  const containerClassName = classNames('EntityPickerItem', className);
  const labelClassName = classNames('EntityPickerItem__label', {
    'EntityPickerItem__label--disabled': disabled
  });
  return (
    <div className={containerClassName}>
      <Checkbox onChange={onChange} disabled={disabled} checked={isSelected} id={inputId} />
      <Label htmlFor={inputId} className={labelClassName}>
        {statusText && statusColor && (
          <Tooltip
            tooltipClassName={classNames(
              `${className}__status-tooltip`,
              `${className}__status-tooltip__${statusColor}`,
              'EntityPickerItem__status-tooltip',
              `EntityPickerItem__status-tooltip__${statusColor}`
            )}
            content={statusText}
            matchArrowBackground={true}
          >
            <StatusColorIndicator
              statusColor={statusColor}
              classNames="EntityPickerItem__status-indicator"
            />
          </Tooltip>
        )}
        {userFriendlyId && <span className="EntityPickerItem__identifier">{userFriendlyId}</span>}
        <OverflowTooltip
          getTargetProps={() => ({ className: 'EntityPickerItem__title' })}
          tooltipClassName="EntityPickerItem__title-tooltip"
          singleLine={true}
          autoTipOnEllipsis={true}
          triggerTarget={Tooltip.TriggerTargets.TOOLTIP}
          theme={Tooltip.Themes.DARK}
        >
          {title}
        </OverflowTooltip>
      </Label>
    </div>
  );
};

EntityPickerItem.propTypes = {
  /**
   * The container class name.
   */
  className: PropTypes.string,
  /**
   * The entity type.
   */
  entityType: PropTypes.oneOf(objectValues(ENTITY_TYPES)).isRequired,
  /**
   * The entity ID.
   */
  id: PropTypes.string.isRequired,
  /**
   * The title of the entity.
   */
  title: PropTypes.string.isRequired,
  /**
   * A user friendly ID if exists.
   */
  userFriendlyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * The status color of the entity. On of Colors values.
   */
  statusColor: PropTypes.oneOf(objectValues(Colors)),
  /**
   * The status text of the entity.
   */
  statusText: PropTypes.string,
  /**
   * Whether this entity is selected or not.
   */
  isSelected: PropTypes.bool,
  /**
   * Whether this entity selection is disabled or not.
   */
  disabled: PropTypes.bool,
  /**
   * Handler when changing entity selection.
   */
  onChange: PropTypes.func.isRequired
};

export default EntityPickerItem;
